import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CHAT_THREADS_PAGE, chatApi } from "../../api/chat";
import type { ChatThread } from "../../api/chat";
import { useChat } from "../../components/chat/useChatConnection";
import { useToastError } from "../../components/Toast";
import Ic from "../../components/icons.jsx";
import { ChatAvatar } from "./ChatAvatar";
import { dateTimeOf, foundWhen, plainLine } from "./chatFormat";
import { MessageBody } from "./MessageBody";
import { Clamp, Where, foundLink } from "./SearchResults";

/** The threads in order of their latest reply, newest first, the given ones in place of any of the same message. */
function merge(list: ChatThread[], incoming: ChatThread[]): ChatThread[] {
  const byId = new Map(list.map((x) => [x.message.id, x]));
  for (const x of incoming) byId.set(x.message.id, x);
  const at = (x: ChatThread) => Date.parse(x.message.lastReplyAt ?? x.message.createdAt);
  return [...byId.values()].sort((a, b) => at(b) - at(a) || b.message.id.localeCompare(a.message.id));
}

/**
 * Slack's "Threads": the threads the user follows, the latest reply first, each with its channel, its message, the
 * newest reply and how many replies are new. A thread opens in its channel, at its message, with the thread beside
 * it; on a phone Back comes here again. A reply in a followed thread, a channel read or a thread left elsewhere
 * changes the list at once.
 */
export function ThreadsList({ narrow }: { narrow: boolean }) {
  const { t } = useTranslation();
  const { channels, people, me, subscribe } = useChat();
  const navigate = useNavigate();
  const toastError = useToastError();
  const [threads, setThreads] = useState<ChatThread[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [more, setMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  // `reset` starts the list over (a thread left, a channel gone); otherwise the first page is laid over what is shown.
  const refresh = useCallback(async (reset: boolean) => {
    try {
      const page = await chatApi.threads();
      setThreads((list) => (reset || list === null ? page : merge(list, page)));
      if (reset) setMore(page.length >= CHAT_THREADS_PAGE);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => { void refresh(true); }, [refresh]);

  useEffect(() => {
    const later = (reset: boolean) => {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void refresh(reset), 300);
    };
    const off = subscribe((ev) => {
      if (ev.type === "message") {
        if (ev.message.parentId !== null && ev.message.followers.includes(me)) later(false);
      } else if (ev.type === "updated") {
        const m = ev.message;
        setThreads((list) => list?.map((x) => (x.message.id === m.id ? { ...x, message: m }
          : x.latest?.id === m.id ? { ...x, latest: m } : x)) ?? list);
      } else if (ev.type === "read") {
        setThreads((list) => list?.map((x) => (x.message.channelId === ev.channelId ? { ...x, unread: 0 } : x)) ?? list);
      } else if (ev.type === "deleted") {
        if (ev.parentId !== null) later(false);
        else setThreads((list) => list?.filter((x) => x.message.id !== ev.messageId) ?? list);
      } else if (ev.type === "followed" || ev.type === "channels") {
        later(true);
      } else if (ev.type === "ready") {
        later(false);
      }
    });
    return () => {
      off();
      window.clearTimeout(timer.current);
    };
  }, [subscribe, me, refresh]);

  async function loadMore() {
    const last = threads?.[threads.length - 1];
    if (!last || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await chatApi.threads(last.message.id);
      setThreads((list) => merge(list ?? [], page));
      setMore(page.length >= CHAT_THREADS_PAGE);
    } catch (e) {
      toastError(e);
    } finally {
      setLoadingMore(false);
    }
  }

  const byId = new Map((channels ?? []).map((c) => [c.id, c]));
  const unknown = t("chat.unknownPerson", "former colleague");
  const nameOf = (id: string) => people.get(id)?.name ?? unknown;
  const now = new Date();

  return (
    <section className="chat-main chat-threads" aria-label={t("chat.threads", "Threads")}>
      <header className="chat-head">
        {narrow && (
          <button type="button" className="chat-tool chat-back" onClick={() => navigate("/chat")}
            aria-label={t("chat.back", "Back to channels")}>
            <Ic.arrowLeft />
          </button>
        )}
        <div className="chat-head-title">
          <Ic.threads />
          <h1>{t("chat.threads", "Threads")}</h1>
        </div>
        <p className="chat-head-topic">{t("chat.threadsTopic", "Threads you follow, the latest reply first")}</p>
      </header>

      <div className="chat-search-scroll">
        {threads === null && !failed && (
          <div className="chat-hits" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="chat-hit skeleton">
                <span className="chat-skel line short" />
                <span className="chat-skel line" />
                <span className="chat-skel line" />
              </div>
            ))}
          </div>
        )}
        {failed && threads === null && (
          <p className="chat-error chat-pad">{t("chat.threadsFailed", "The threads could not be loaded. Try again.")}</p>
        )}
        {threads !== null && threads.length === 0 && (
          <div className="chat-search-state">
            <div className="chat-intro-mark"><Ic.threads /></div>
            <h2>{t("chat.threadsEmptyTitle", "You follow no thread yet")}</h2>
            <p>
              {t("chat.threadsEmptyText",
                "You follow a thread when you reply in it, when someone mentions you there, or when you click Follow in it. Its new replies then show here.")}
            </p>
          </div>
        )}
        {threads !== null && threads.length > 0 && (
          <ol className="chat-hits">
            {threads.map(({ message: m, latest, unread }) => (
              <li key={m.id}>
                <Link className={`chat-hit${unread > 0 ? " unread" : ""}`} to={foundLink(latest ?? m)} state={{ fromSearch: true }}>
                  <div className="chat-hit-where">
                    <Where channel={byId.get(m.channelId)} people={people} />
                    {unread > 0 && (
                      <span className="chat-thread-new">{t("chat.threadsNew", "{{count}} new replies", { count: unread })}</span>
                    )}
                    {m.lastReplyAt && (
                      <time className="chat-hit-time" dateTime={m.lastReplyAt} title={dateTimeOf(m.lastReplyAt)}>
                        {foundWhen(m.lastReplyAt, now)}
                      </time>
                    )}
                  </div>
                  <div className="chat-hit-msg">
                    <ChatAvatar person={people.get(m.authorId)} id={m.authorId} />
                    <div className="chat-hit-main">
                      <span className="chat-msg-author">{nameOf(m.authorId)}</span>
                      <Clamp>
                        <MessageBody body={m.body} people={people} me={me} plain />
                      </Clamp>
                      {latest && (
                        <div className="chat-thread-latest">
                          <ChatAvatar person={people.get(latest.authorId)} id={latest.authorId} size="xs" />
                          <span className="chat-thread-latest-text">
                            <b>{nameOf(latest.authorId)}</b>{" "}
                            {plainLine(latest.body, people, unknown)
                              || `📎 ${latest.attachments.map((a) => a.filename).join(", ")}`}
                          </span>
                        </div>
                      )}
                      <div className="chat-thread-meta">{t("chat.replies", "{{count}} replies", { count: m.replyCount })}</div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ol>
        )}
        {threads !== null && threads.length > 0 && more && (
          <div className="chat-search-more">
            <button type="button" className="btn sm" disabled={loadingMore} onClick={() => void loadMore()}>
              {loadingMore ? t("chat.loading", "Loading…") : t("chat.searchMore", "Show more")}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * How many followed threads have replies new to the user, for the count beside "Threads". It is read again when a
 * reply in a followed thread comes, a thread is followed or left, or a channel is read while something is new.
 */
export function useUnreadThreads(): number {
  const { subscribe, me } = useChat();
  const [count, setCount] = useState(0);
  const counted = useRef(0);
  counted.current = count;

  useEffect(() => {
    let live = true;
    let timer: number | undefined;
    const load = () => {
      chatApi.unreadThreads().then((r) => { if (live) setCount(r.count); }).catch(() => {});
    };
    const later = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(load, 300);
    };
    load();
    const off = subscribe((ev) => {
      const reply = ev.type === "message" && ev.message.parentId !== null && ev.message.authorId !== me
        && ev.message.followers.includes(me);
      // Reading a channel only takes away; with nothing new there is nothing to take.
      const read = ev.type === "read" && counted.current > 0;
      const gone = ev.type === "deleted" && ev.parentId !== null && counted.current > 0;
      if (reply || read || gone || ev.type === "followed" || ev.type === "channels" || ev.type === "ready") later();
    });
    return () => {
      live = false;
      window.clearTimeout(timer);
      off();
    };
  }, [subscribe, me]);

  return count;
}
